import { createError } from '@common/neo.tax';

import {
  RdProjectTeamMemberSortFields,
  SortDirection,
  useCursorPaginatedQuery,
  PeriodName,
  GetRdProjectTeamMembersQuery,
  GET_RD_PROJECT_TEAM_MEMBERS,
  GetRdProjectTeamMembersQueryVariables,
} from '@neo.tax/shared';

export type RdProjectTeamMember = GetRdProjectTeamMembersQuery['getRdProjectTeamMembers']['edges'][number]['node'];

export type UseRdProjectTeamMembersResult = {
  loading: boolean;
  isLoadingMore: boolean;
  hasNextPage: boolean;
  fetchMore: () => Promise<void>;
  rdProjectTeamMembers: RdProjectTeamMember[] | null;
};

/**
 * Hook used to get all R&D project team members in alphabetical order
 */
export const useRdProjectTeamMembers = ({
  debounceKey,
  fiscalYearId,
  hasAllocation,
  numElementsPerFetch = 30,
  periodName,
  rdProjectId,
  searchKeyword,
}: {
  debounceKey?: string;
  hasAllocation?: boolean | undefined;
  periodName: PeriodName;
  rdProjectId: string;
  searchKeyword?: string | null;
  fiscalYearId: string;
  numElementsPerFetch?: number;
}): UseRdProjectTeamMembersResult => {
  const filter = {
    allocationPercent: hasAllocation === undefined ? undefined : hasAllocation ? { gt: 0 } : { eq: 0 },
    contributorName: !searchKeyword || searchKeyword.length === 0 ? undefined : { iLike: `%${searchKeyword}%` },
  };
  const sorting = {
    field: RdProjectTeamMemberSortFields.ContributorName,
    direction: SortDirection.Asc,
  };

  const { data, error, hasNextPage, isLoadingMore, loading, onLoadMoreItems } = useCursorPaginatedQuery<
    GetRdProjectTeamMembersQuery,
    GetRdProjectTeamMembersQueryVariables
  >(
    GET_RD_PROJECT_TEAM_MEMBERS,
    {
      variables: {
        rdProjectId,
        fiscalYearId,
        filter,
        sorting,
      },
      context: {
        debounceKey,
      },
    },
    {
      getConnection: (data) => data.getRdProjectTeamMembers,
      numElementsPerFetch,
    },
  );

  const rdProjectTeamMembers = data?.getRdProjectTeamMembers?.edges.map((edge) => edge.node) ?? null;

  if (loading) {
    return {
      loading: true,
      isLoadingMore: false,
      hasNextPage,
      rdProjectTeamMembers,
      fetchMore: onLoadMoreItems,
    };
  }
  if (error) {
    throw error;
  }
  if (!data) {
    throw createError(
      'MissingRdProjectTeamMembers',
      `rdProjectId: ${rdProjectId}, fiscalYearId: ${fiscalYearId}, periodName: ${periodName}, searchKeyword: ${searchKeyword}, hasAllocation: ${hasAllocation}`,
    );
  }

  return {
    loading,
    isLoadingMore,
    hasNextPage,
    rdProjectTeamMembers,
    fetchMore: onLoadMoreItems,
  };
};
